// src/lib/stack-app/apps/implementations/client-app-impl.ts
import { WebAuthnError, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { KnownErrors, StackClientInterface } from "@stackframe/stack-shared";
import { InternalSession } from "@stackframe/stack-shared/dist/sessions";
import { encodeBase32 } from "@stackframe/stack-shared/dist/utils/bytes";
import { scrambleDuringCompileTime } from "@stackframe/stack-shared/dist/utils/compile-time";
import { isBrowserLike } from "@stackframe/stack-shared/dist/utils/env";
import { StackAssertionError, captureError, throwErr } from "@stackframe/stack-shared/dist/utils/errors";
import { parseJson } from "@stackframe/stack-shared/dist/utils/json";
import { DependenciesMap } from "@stackframe/stack-shared/dist/utils/maps";
import { deepPlainEquals, omit } from "@stackframe/stack-shared/dist/utils/objects";
import { neverResolve, runAsynchronously, wait } from "@stackframe/stack-shared/dist/utils/promises";
import { suspend, suspendIfSsr, use } from "@stackframe/stack-shared/dist/utils/react";
import { Result } from "@stackframe/stack-shared/dist/utils/results";
import { Store, storeLock } from "@stackframe/stack-shared/dist/utils/stores";
import { deindent, mergeScopeStrings } from "@stackframe/stack-shared/dist/utils/strings";
import { getRelativePart, isRelative } from "@stackframe/stack-shared/dist/utils/urls";
import { generateUuid } from "@stackframe/stack-shared/dist/utils/uuids";
import * as cookie from "cookie";
import * as NextNavigationUnscrambled from "next/navigation";
import React, { useCallback, useMemo } from "react";
import { constructRedirectUrl } from "../../../../utils/url.js";
import { addNewOAuthProviderOrScope, callOAuthCallback, signInWithOAuth } from "../../../auth.js";
import { createBrowserCookieHelper, createCookieHelper, createPlaceholderCookieHelper, deleteCookie, deleteCookieClient, isSecure as isSecureCookieContext, setOrDeleteCookie, setOrDeleteCookieClient } from "../../../cookie.js";
import { apiKeyCreationOptionsToCrud } from "../../api-keys/index.js";
import { stackAppInternalsSymbol } from "../../common.js";
import { contactChannelCreateOptionsToCrud, contactChannelUpdateOptionsToCrud } from "../../contact-channels/index.js";
import { adminProjectCreateOptionsToCrud } from "../../projects/index.js";
import { teamCreateOptionsToCrud, teamUpdateOptionsToCrud } from "../../teams/index.js";
import { userUpdateOptionsToCrud, withUserDestructureGuard } from "../../users/index.js";
import { clientVersion, createCache, createCacheBySession, createEmptyTokenStore, getBaseUrl, getDefaultExtraRequestHeaders, getDefaultProjectId, getDefaultPublishableClientKey, getUrls, resolveConstructorOptions } from "./common.js";
import { useAsyncCache } from "./common.js";
import * as sc from "@stackframe/stack-sc";
import { cookies } from "@stackframe/stack-sc";
var isReactServer2 = false;
isReactServer2 = sc.isReactServer;
var NextNavigation = scrambleDuringCompileTime(NextNavigationUnscrambled);
var allClientApps = /* @__PURE__ */ new Map();
var __StackClientAppImplIncomplete = class __StackClientAppImplIncomplete {
  constructor(options, extraOptions) {
    this._uniqueIdentifier = void 0;
    this.__DEMO_ENABLE_SLIGHT_FETCH_DELAY = false;
    this._ownedAdminApps = new DependenciesMap();
    this._currentUserCache = createCacheBySession(async (session) => {
      if (this.__DEMO_ENABLE_SLIGHT_FETCH_DELAY) {
        await wait(2e3);
      }
      if (session.isKnownToBeInvalid()) {
        return null;
      }
      return await this._interface.getClientUserByToken(session);
    });
    this._currentProjectCache = createCache(async () => {
      return Result.orThrow(await this._interface.getClientProject());
    });
    this._ownedProjectsCache = createCacheBySession(async (session) => {
      return await this._interface.listProjects(session);
    });
    this._currentUserPermissionsCache = createCacheBySession(async (session, [teamId, recursive]) => {
      return await this._interface.listCurrentUserTeamPermissions({ teamId, recursive }, session);
    });
    this._currentUserProjectPermissionsCache = createCacheBySession(async (session, [recursive]) => {
      return await this._interface.listCurrentUserProjectPermissions({ recursive }, session);
    });
    this._currentUserTeamsCache = createCacheBySession(async (session) => {
      return await this._interface.listCurrentUserTeams(session);
    });
    this._currentUserOAuthConnectionAccessTokensCache = createCacheBySession(
      async (session, [providerId, scope]) => {
        try {
          const result = await this._interface.createProviderAccessToken(providerId, scope || "", session);
          return { accessToken: result.access_token };
        } catch (err) {
          if (!(KnownErrors.OAuthConnectionDoesNotHaveRequiredScope.isInstance(err) || KnownErrors.OAuthConnectionNotConnectedToUser.isInstance(err))) {
            throw err;
          }
        }
        return null;
      }
    );
    this._currentUserOAuthConnectionCache = createCacheBySession(
      async (session, [providerId, scope, redirect]) => {
        return await this._getUserOAuthConnectionCacheFn({
          getUser: async () => Result.orThrow(await this._currentUserCache.getOrWait([session], "write-only")),
          getOrWaitOAuthToken: async () => Result.orThrow(await this._currentUserOAuthConnectionAccessTokensCache.getOrWait([session, providerId, scope || ""], "write-only")),
          useOAuthToken: () => useAsyncCache(this._currentUserOAuthConnectionAccessTokensCache, [session, providerId, scope || ""], "connection.useAccessToken()"),
          providerId,
          scope,
          redirect,
          session
        });
      }
    );
    this._teamMemberProfilesCache = createCacheBySession(
      async (session, [teamId]) => {
        return await this._interface.listTeamMemberProfiles({ teamId }, session);
      }
    );
    this._teamInvitationsCache = createCacheBySession(
      async (session, [teamId]) => {
        return await this._interface.listTeamInvitations({ teamId }, session);
      }
    );
    this._currentUserTeamProfileCache = createCacheBySession(
      async (session, [teamId]) => {
        return await this._interface.getTeamMemberProfile({ teamId, userId: "me" }, session);
      }
    );
    this._clientContactChannelsCache = createCacheBySession(
      async (session) => {
        return await this._interface.listClientContactChannels(session);
      }
    );
    this._userApiKeysCache = createCacheBySession(
      async (session) => {
        const results = await this._interface.listProjectApiKeys({ user_id: "me" }, session, "client");
        return results;
      }
    );
    this._teamApiKeysCache = createCacheBySession(
      async (session, [teamId]) => {
        const results = await this._interface.listProjectApiKeys({ team_id: teamId }, session, "client");
        return results;
      }
    );
    this._notificationCategoriesCache = createCacheBySession(
      async (session) => {
        const results = await this._interface.listNotificationCategories(session);
        return results;
      }
    );
    this._currentUserOAuthProvidersCache = createCacheBySession(
      async (session) => {
        return await this._interface.listOAuthProviders({ user_id: "me" }, session);
      }
    );
    this._userItemCache = createCacheBySession(
      async (session, [userId, itemId]) => {
        return await this._interface.getItem({ userId, itemId }, session);
      }
    );
    this._teamItemCache = createCacheBySession(
      async (session, [teamId, itemId]) => {
        return await this._interface.getItem({ teamId, itemId }, session);
      }
    );
    this._customItemCache = createCacheBySession(
      async (session, [customCustomerId, itemId]) => {
        return await this._interface.getItem({ customCustomerId, itemId }, session);
      }
    );
    this._userProductsCache = createCacheBySession(
      async (session, [userId, cursor, limit]) => {
        return await this._interface.listProducts({
          customer_type: "user",
          customer_id: userId,
          cursor: cursor ?? void 0,
          limit: limit ?? void 0
        }, session);
      }
    );
    this._teamProductsCache = createCacheBySession(
      async (session, [teamId, cursor, limit]) => {
        return await this._interface.listProducts({
          customer_type: "team",
          customer_id: teamId,
          cursor: cursor ?? void 0,
          limit: limit ?? void 0
        }, session);
      }
    );
    this._customProductsCache = createCacheBySession(
      async (session, [customCustomerId, cursor, limit]) => {
        return await this._interface.listProducts({
          customer_type: "custom",
          customer_id: customCustomerId,
          cursor: cursor ?? void 0,
          limit: limit ?? void 0
        }, session);
      }
    );
    this._userInvoicesCache = createCacheBySession(
      async (session, [userId, cursor, limit]) => {
        return await this._interface.listInvoices({
          customer_type: "user",
          customer_id: userId,
          cursor: cursor ?? void 0,
          limit: limit ?? void 0
        }, session);
      }
    );
    this._teamInvoicesCache = createCacheBySession(
      async (session, [teamId, cursor, limit]) => {
        return await this._interface.listInvoices({
          customer_type: "team",
          customer_id: teamId,
          cursor: cursor ?? void 0,
          limit: limit ?? void 0
        }, session);
      }
    );
    this._customerBillingCache = createCacheBySession(
      async (session, [customerType, customerId]) => {
        return await this._interface.getCustomerBilling(customerType, customerId, session);
      }
    );
    this._convexPartialUserCache = createCache(
      async ([ctx]) => await this._getPartialUserFromConvex(ctx)
    );
    this._trustedParentDomainCache = createCache(
      async ([domain]) => await this._getTrustedParentDomain(domain)
    );
    this._anonymousSignUpInProgress = null;
    this._memoryTokenStore = createEmptyTokenStore();
    this._nextServerCookiesTokenStores = /* @__PURE__ */ new WeakMap();
    this._requestTokenStores = /* @__PURE__ */ new WeakMap();
    this._storedBrowserCookieTokenStore = null;
    this._mostRecentQueuedCookieRefreshIndex = 0;
    /**
     * A map from token stores and session keys to sessions.
     *
     * This isn't just a map from session keys to sessions for two reasons:
     *
     * - So we can garbage-collect Session objects when the token store is garbage-collected
     * - So different token stores are separated and don't leak information between each other, eg. if the same user sends two requests to the same server they should get a different session object
     */
    this._sessionsByTokenStoreAndSessionKey = /* @__PURE__ */ new WeakMap();
    const resolvedOptions = resolveConstructorOptions(options);
    if (!__StackClientAppImplIncomplete.LazyStackAdminAppImpl.value) {
      throw new StackAssertionError("Admin app implementation not initialized. Did you import the _StackClientApp from stack-app/apps/implementations/index.ts? You can't import it directly from ./apps/implementations/client-app-impl.ts as that causes a circular dependency (see the comment at _LazyStackAdminAppImpl for more details).");
    }
    this._options = resolvedOptions;
    this._extraOptions = extraOptions;
    const projectId = resolvedOptions.projectId ?? getDefaultProjectId();
    if (projectId !== "internal" && !projectId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i)) {
      throw new Error(`Invalid project ID: ${projectId}. Project IDs must be UUIDs. Please check your environment variables and/or your StackApp.`);
    }
    if (extraOptions && extraOptions.interface) {
      this._interface = extraOptions.interface;
    } else {
      this._interface = new StackClientInterface({
        getBaseUrl: () => getBaseUrl(resolvedOptions.baseUrl),
        extraRequestHeaders: resolvedOptions.extraRequestHeaders ?? getDefaultExtraRequestHeaders(),
        projectId,
        clientVersion,
        publishableClientKey: resolvedOptions.publishableClientKey ?? getDefaultPublishableClientKey(),
        prepareRequest: async () => {
          await cookies?.();
        }
      });
    }
    this._tokenStoreInit = resolvedOptions.tokenStore;
    this._redirectMethod = resolvedOptions.redirectMethod || "none";
    this._redirectMethod = resolvedOptions.redirectMethod || "nextjs";
    this._urlOptions = resolvedOptions.urls ?? {};
    this._oauthScopesOnSignIn = resolvedOptions.oauthScopesOnSignIn ?? {};
    if (extraOptions && extraOptions.uniqueIdentifier) {
      this._uniqueIdentifier = extraOptions.uniqueIdentifier;
      this._initUniqueIdentifier();
    }
  }
  async _createCookieHelper() {
    if (this._tokenStoreInit === "nextjs-cookie" || this._tokenStoreInit === "cookie") {
      return await createCookieHelper();
    } else {
      return await createPlaceholderCookieHelper();
    }
  }
  async _getUserOAuthConnectionCacheFn(options) {
    const user = await options.getUser();
    let hasConnection = true;
    if (!user || !user.oauth_providers.find((p) => p.id === options.providerId)) {
      hasConnection = false;
    }
    const token = await options.getOrWaitOAuthToken();
    if (!token) {
      hasConnection = false;
    }
    if (!hasConnection && options.redirect) {
      if (!options.session) {
        throw new Error(deindent`
          Cannot add new scopes to a user that is not a CurrentUser. Please ensure that you are calling this function on a CurrentUser object, or remove the 'or: redirect' option.

          Often, you can solve this by calling this function in the browser instead, or by removing the 'or: redirect' option and dealing with the case where the user doesn't have enough permissions.
        `);
      }
      await addNewOAuthProviderOrScope(
        this._interface,
        {
          provider: options.providerId,
          redirectUrl: this.urls.oauthCallback,
          errorRedirectUrl: this.urls.error,
          providerScope: mergeScopeStrings(options.scope || "", (this._oauthScopesOnSignIn[options.providerId] ?? []).join(" "))
        },
        options.session
      );
      return await neverResolve();
    } else if (!hasConnection) {
      return null;
    }
    return {
      id: options.providerId,
      async getAccessToken() {
        const result = await options.getOrWaitOAuthToken();
        if (!result) {
          throw new StackAssertionError("No access token available");
        }
        return result;
      },
      useAccessToken() {
        const result = options.useOAuthToken();
        if (!result) {
          throw new StackAssertionError("No access token available");
        }
        return result;
      }
    };
  }
  _initUniqueIdentifier() {
    if (!this._uniqueIdentifier) {
      throw new StackAssertionError("Unique identifier not initialized");
    }
    if (allClientApps.has(this._uniqueIdentifier)) {
      throw new StackAssertionError("A Stack client app with the same unique identifier already exists");
    }
    allClientApps.set(this._uniqueIdentifier, [this._extraOptions?.checkString ?? void 0, this]);
  }
  /**
   * Cloudflare workers does not allow use of randomness on the global scope (on which the Stack app is probably
   * initialized). For that reason, we generate the unique identifier lazily when it is first needed instead of in the
   * constructor.
   */
  _getUniqueIdentifier() {
    if (!this._uniqueIdentifier) {
      this._uniqueIdentifier = generateUuid();
      this._initUniqueIdentifier();
    }
    return this._uniqueIdentifier;
  }
  async _checkFeatureSupport(name, options) {
    return await this._interface.checkFeatureSupport({ ...options, name });
  }
  _useCheckFeatureSupport(name, options) {
    runAsynchronously(this._checkFeatureSupport(name, options));
    throw new StackAssertionError(`${name} is not currently supported. Please reach out to Stack support for more information.`);
  }
  get _legacyRefreshTokenCookieName() {
    return `stack-refresh-${this.projectId}`;
  }
  get _refreshTokenCookieName() {
    return `stack-refresh-${this.projectId}`;
  }
  _getRefreshTokenDefaultCookieNameForSecure(secure) {
    return `${secure ? "__Host-" : ""}${this._refreshTokenCookieName}--default`;
  }
  _getCustomRefreshCookieName(domain) {
    const encoded = encodeBase32(new TextEncoder().encode(domain.toLowerCase()));
    return `${this._refreshTokenCookieName}--custom-${encoded}`;
  }
  _formatRefreshCookieValue(refreshToken, updatedAt) {
    return JSON.stringify({
      refresh_token: refreshToken,
      updated_at_millis: updatedAt
    });
  }
  _formatAccessCookieValue(refreshToken, accessToken) {
    return refreshToken && accessToken ? JSON.stringify([refreshToken, accessToken]) : null;
  }
  _parseStructuredRefreshCookie(value) {
    if (!value) {
      return null;
    }
    const parsed = parseJson(value);
    if (parsed.status !== "ok" || typeof parsed.data !== "object" || parsed.data === null) {
      console.warn("Failed to parse structured refresh cookie");
      return null;
    }
    const data = parsed.data;
    const refreshToken = "refresh_token" in data && typeof data.refresh_token === "string" ? data.refresh_token : null;
    const updatedAt = "updated_at_millis" in data && typeof data.updated_at_millis === "number" ? data.updated_at_millis : null;
    if (!refreshToken) {
      console.warn("Refresh token not found in structured refresh cookie");
      return null;
    }
    return {
      refreshToken,
      updatedAt
    };
  }
  _extractRefreshTokenFromCookieMap(cookies2) {
    const { legacyNames, structuredPrefixes } = this._getRefreshTokenCookieNamePatterns();
    for (const name of legacyNames) {
      const value = cookies2[name];
      if (value) {
        return { refreshToken: value, updatedAt: null };
      }
    }
    let selected = null;
    for (const [name, value] of Object.entries(cookies2)) {
      if (!structuredPrefixes.some((prefix) => name.startsWith(prefix))) continue;
      const parsed = this._parseStructuredRefreshCookie(value);
      if (!parsed) continue;
      const candidateUpdatedAt = parsed.updatedAt ?? Number.NEGATIVE_INFINITY;
      const selectedUpdatedAt = selected?.updatedAt ?? Number.NEGATIVE_INFINITY;
      if (!selected || candidateUpdatedAt > selectedUpdatedAt) {
        selected = parsed;
      }
    }
    if (!selected) {
      return { refreshToken: null, updatedAt: null };
    }
    return {
      refreshToken: selected.refreshToken,
      updatedAt: selected.updatedAt ?? null
    };
  }
  _getTokensFromCookies(cookies2) {
    const { refreshToken } = this._extractRefreshTokenFromCookieMap(cookies2);
    const accessTokenCookie = cookies2[this._accessTokenCookieName] ?? null;
    let accessToken = null;
    if (accessTokenCookie && accessTokenCookie.startsWith('["')) {
      const parsed = parseJson(accessTokenCookie);
      if (parsed.status === "ok" && typeof parsed.data === "object" && parsed.data !== null && Array.isArray(parsed.data) && parsed.data.length === 2 && typeof parsed.data[0] === "string" && typeof parsed.data[1] === "string") {
        if (parsed.data[0] === refreshToken) {
          accessToken = parsed.data[1];
        }
      } else {
        console.warn("Access token cookie has invalid format");
      }
    }
    return {
      refreshToken,
      accessToken
    };
  }
  get _accessTokenCookieName() {
    return `stack-access`;
  }
  _getAllBrowserCookies() {
    if (!isBrowserLike()) {
      throw new StackAssertionError("Cannot get browser cookies on the server!");
    }
    return cookie.parseCookie(document.cookie || "");
  }
  _getRefreshTokenCookieNamePatterns() {
    return {
      legacyNames: [this._legacyRefreshTokenCookieName, "stack-refresh"],
      structuredPrefixes: [
        `${this._refreshTokenCookieName}--`,
        `__Host-${this._refreshTokenCookieName}--`
      ]
    };
  }
  _collectRefreshTokenCookieNames(cookies2) {
    const { legacyNames, structuredPrefixes } = this._getRefreshTokenCookieNamePatterns();
    const names = /* @__PURE__ */ new Set();
    for (const name of legacyNames) {
      if (cookies2[name]) {
        names.add(name);
      }
    }
    for (const name of Object.keys(cookies2)) {
      if (structuredPrefixes.some((prefix) => name.startsWith(prefix))) {
        names.add(name);
      }
    }
    return names;
  }
  _prepareRefreshCookieUpdate(existingCookies, refreshToken, accessToken, defaultCookieName) {
    const cookieNames = this._collectRefreshTokenCookieNames(existingCookies);
    cookieNames.delete(defaultCookieName);
    const updatedAt = refreshToken ? Date.now() : null;
    const refreshCookieValue = refreshToken && updatedAt !== null ? this._formatRefreshCookieValue(refreshToken, updatedAt) : null;
    const accessTokenPayload = this._formatAccessCookieValue(refreshToken, accessToken);
    return {
      updatedAt,
      refreshCookieValue,
      accessTokenPayload,
      cookieNamesToDelete: [...cookieNames]
    };
  }
  _queueCustomRefreshCookieUpdate(refreshToken, updatedAt, context) {
    runAsynchronously(async () => {
      this._mostRecentQueuedCookieRefreshIndex++;
      const updateIndex = this._mostRecentQueuedCookieRefreshIndex;
      let hostname;
      if (isBrowserLike()) {
        hostname = window.location.hostname;
      } else {
        hostname = (await sc.headers?.())?.get("host");
      }
      if (!hostname) {
        console.warn("No hostname found when queueing custom refresh cookie update");
        return;
      }
      const domain = await this._trustedParentDomainCache.getOrWait([hostname], "read-write");
      const cookieOptions = { maxAge: 60 * 60 * 24 * 365, noOpIfServerComponent: true };
      const setCookie = async (targetDomain, value2) => {
        const name = this._getCustomRefreshCookieName(targetDomain);
        const options = { ...cookieOptions, domain: targetDomain };
        if (context === "browser") {
          setOrDeleteCookieClient(name, value2, options);
        } else {
          await setOrDeleteCookie(name, value2, options);
        }
      };
      if (domain.status === "error" || !domain.data || updateIndex !== this._mostRecentQueuedCookieRefreshIndex) {
        return;
      }
      const value = refreshToken && updatedAt ? this._formatRefreshCookieValue(refreshToken, updatedAt) : null;
      await setCookie(domain.data, value);
      const isSecure = await isSecureCookieContext();
      await setOrDeleteCookie(this._getRefreshTokenDefaultCookieNameForSecure(isSecure), null, cookieOptions);
    });
  }
  async _getTrustedParentDomain(currentDomain) {
    const project = Result.orThrow(await this._interface.getClientProject());
    const domains = project.config.domains.map((d) => d.domain.trim().replace(/^https?:\/\//, "").split("/")[0]?.toLowerCase());
    const trustedWildcards = domains.filter((d) => d.startsWith("**."));
    const parts = currentDomain.split(".");
    for (let i = parts.length - 2; i >= 0; i--) {
      const parentDomain = parts.slice(i).join(".");
      if (domains.includes(parentDomain) && trustedWildcards.includes("**." + parentDomain)) {
        return parentDomain;
      }
    }
    return null;
  }
  _getBrowserCookieTokenStore() {
    if (!isBrowserLike()) {
      throw new Error("Cannot use cookie token store on the server!");
    }
    if (this._storedBrowserCookieTokenStore === null) {
      const getCurrentValue = (old) => {
        const tokens = this._getTokensFromCookies(this._getAllBrowserCookies());
        return {
          refreshToken: tokens.refreshToken,
          accessToken: tokens.accessToken ?? (old?.refreshToken === tokens.refreshToken ? old.accessToken : null)
        };
      };
      this._storedBrowserCookieTokenStore = new Store(getCurrentValue(null));
      let hasSucceededInWriting = true;
      setInterval(() => {
        if (hasSucceededInWriting) {
          const oldValue = this._storedBrowserCookieTokenStore.get();
          const currentValue = getCurrentValue(oldValue);
          if (!deepPlainEquals(currentValue, oldValue)) {
            this._storedBrowserCookieTokenStore.set(currentValue);
          }
        }
      }, 100);
      this._storedBrowserCookieTokenStore.onChange((value) => {
        try {
          const refreshToken = value.refreshToken;
          const secure = window.location.protocol === "https:";
          const defaultName = this._getRefreshTokenDefaultCookieNameForSecure(secure);
          const { updatedAt, refreshCookieValue, accessTokenPayload, cookieNamesToDelete } = this._prepareRefreshCookieUpdate(
            this._getAllBrowserCookies(),
            refreshToken,
            value.accessToken ?? null,
            defaultName
          );
          setOrDeleteCookieClient(defaultName, refreshCookieValue, { maxAge: 60 * 60 * 24 * 365, secure });
          setOrDeleteCookieClient(this._accessTokenCookieName, accessTokenPayload, { maxAge: 60 * 60 * 24 });
          cookieNamesToDelete.forEach((name) => deleteCookieClient(name, {}));
          this._queueCustomRefreshCookieUpdate(refreshToken, updatedAt, "browser");
          hasSucceededInWriting = true;
        } catch (e) {
          if (!isBrowserLike()) {
            hasSucceededInWriting = false;
          } else {
            throw e;
          }
        }
      });
    }
    return this._storedBrowserCookieTokenStore;
  }
  _getOrCreateTokenStore(cookieHelper, overrideTokenStoreInit) {
    const tokenStoreInit = overrideTokenStoreInit === void 0 ? this._tokenStoreInit : overrideTokenStoreInit;
    switch (tokenStoreInit) {
      case "cookie": {
        return this._getBrowserCookieTokenStore();
      }
      case "nextjs-cookie": {
        if (isBrowserLike()) {
          return this._getBrowserCookieTokenStore();
        } else {
          const tokens = this._getTokensFromCookies(cookieHelper.getAll());
          const store = new Store(tokens);
          store.onChange((value) => {
            runAsynchronously(async () => {
              const refreshToken = value.refreshToken;
              const secure = await isSecureCookieContext();
              const defaultName = this._getRefreshTokenDefaultCookieNameForSecure(secure);
              const { updatedAt, refreshCookieValue, accessTokenPayload, cookieNamesToDelete } = this._prepareRefreshCookieUpdate(
                cookieHelper.getAll(),
                refreshToken,
                value.accessToken ?? null,
                defaultName
              );
              await Promise.all([
                setOrDeleteCookie(defaultName, refreshCookieValue, { maxAge: 60 * 60 * 24 * 365, noOpIfServerComponent: true }),
                setOrDeleteCookie(this._accessTokenCookieName, accessTokenPayload, { maxAge: 60 * 60 * 24, noOpIfServerComponent: true })
              ]);
              if (cookieNamesToDelete.length > 0) {
                await Promise.all(
                  cookieNamesToDelete.map(
                    (name) => deleteCookie(name, { noOpIfServerComponent: true })
                  )
                );
              }
              this._queueCustomRefreshCookieUpdate(refreshToken, updatedAt, "server");
            });
          });
          return store;
        }
      }
      case "memory": {
        return this._memoryTokenStore;
      }
      default: {
        if (tokenStoreInit === null) {
          return createEmptyTokenStore();
        } else if (typeof tokenStoreInit === "object" && "headers" in tokenStoreInit) {
          if (this._requestTokenStores.has(tokenStoreInit)) return this._requestTokenStores.get(tokenStoreInit);
          const stackAuthHeader = tokenStoreInit.headers.get("x-stack-auth");
          if (stackAuthHeader) {
            let parsed2;
            try {
              parsed2 = JSON.parse(stackAuthHeader);
              if (typeof parsed2 !== "object") throw new Error("x-stack-auth header must be a JSON object");
              if (parsed2 === null) throw new Error("x-stack-auth header must not be null");
            } catch (e) {
              throw new Error(`Invalid x-stack-auth header: ${stackAuthHeader}`, { cause: e });
            }
            return this._getOrCreateTokenStore(cookieHelper, {
              accessToken: parsed2.accessToken ?? null,
              refreshToken: parsed2.refreshToken ?? null
            });
          }
          const cookieHeader = tokenStoreInit.headers.get("cookie");
          const parsed = cookie.parseCookie(cookieHeader || "");
          const res = new Store(this._getTokensFromCookies(parsed));
          this._requestTokenStores.set(tokenStoreInit, res);
          return res;
        } else if ("accessToken" in tokenStoreInit || "refreshToken" in tokenStoreInit) {
          return new Store({
            refreshToken: tokenStoreInit.refreshToken,
            accessToken: tokenStoreInit.accessToken
          });
        }
        throw new Error(`Invalid token store ${tokenStoreInit}`);
      }
    }
  }
  _useTokenStore(overrideTokenStoreInit) {
    suspendIfSsr();
    const cookieHelper = createBrowserCookieHelper();
    const tokenStore = this._getOrCreateTokenStore(cookieHelper, overrideTokenStoreInit);
    return tokenStore;
  }
  _getSessionFromTokenStore(tokenStore) {
    const tokenObj = tokenStore.get();
    const sessionKey = InternalSession.calculateSessionKey(tokenObj);
    const existing = sessionKey ? this._sessionsByTokenStoreAndSessionKey.get(tokenStore)?.get(sessionKey) : null;
    if (existing) return existing;
    const session = this._interface.createSession({
      refreshToken: tokenObj.refreshToken,
      accessToken: tokenObj.accessToken
    });
    session.onAccessTokenChange((newAccessToken) => {
      tokenStore.update((old) => ({
        ...old,
        accessToken: newAccessToken?.token ?? null
      }));
    });
    session.onInvalidate(() => {
      tokenStore.update((old) => ({
        ...old,
        accessToken: null,
        refreshToken: null
      }));
    });
    let sessionsBySessionKey = this._sessionsByTokenStoreAndSessionKey.get(tokenStore) ?? /* @__PURE__ */ new Map();
    this._sessionsByTokenStoreAndSessionKey.set(tokenStore, sessionsBySessionKey);
    sessionsBySessionKey.set(sessionKey, session);
    return session;
  }
  async _getSession(overrideTokenStoreInit) {
    const tokenStore = this._getOrCreateTokenStore(await this._createCookieHelper(), overrideTokenStoreInit);
    const session = this._getSessionFromTokenStore(tokenStore);
    return session;
  }
  _useSession(overrideTokenStoreInit) {
    const tokenStore = this._useTokenStore(overrideTokenStoreInit);
    const subscribe = useCallback((cb) => {
      const { unsubscribe } = tokenStore.onChange(() => {
        cb();
      });
      return unsubscribe;
    }, [tokenStore]);
    const getSnapshot = useCallback(() => this._getSessionFromTokenStore(tokenStore), [tokenStore]);
    return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  }
  async _signInToAccountWithTokens(tokens) {
    if (!("accessToken" in tokens) || !("refreshToken" in tokens)) {
      throw new StackAssertionError("Invalid tokens object; can't sign in with this", { tokens });
    }
    const tokenStore = this._getOrCreateTokenStore(await this._createCookieHelper());
    tokenStore.set(tokens);
  }
  _hasPersistentTokenStore(overrideTokenStoreInit) {
    return (overrideTokenStoreInit !== void 0 ? overrideTokenStoreInit : this._tokenStoreInit) !== null;
  }
  _ensurePersistentTokenStore(overrideTokenStoreInit) {
    if (!this._hasPersistentTokenStore(overrideTokenStoreInit)) {
      throw new Error("Cannot call this function on a Stack app without a persistent token store. Make sure the tokenStore option on the constructor is set to a non-null value when initializing Stack.\n\nStack uses token stores to access access tokens of the current user. For example, on web frontends it is commonly the string value 'cookies' for cookie storage.");
    }
  }
  _isInternalProject() {
    return this.projectId === "internal";
  }
  _ensureInternalProject() {
    if (!this._isInternalProject()) {
      throw new Error("Cannot call this function on a Stack app with a project ID other than 'internal'.");
    }
  }
  _clientProjectFromCrud(crud) {
    return {
      id: crud.id,
      displayName: crud.display_name,
      config: {
        signUpEnabled: crud.config.sign_up_enabled,
        credentialEnabled: crud.config.credential_enabled,
        magicLinkEnabled: crud.config.magic_link_enabled,
        passkeyEnabled: crud.config.passkey_enabled,
        clientTeamCreationEnabled: crud.config.client_team_creation_enabled,
        clientUserDeletionEnabled: crud.config.client_user_deletion_enabled,
        allowTeamApiKeys: crud.config.allow_team_api_keys,
        allowUserApiKeys: crud.config.allow_user_api_keys,
        oauthProviders: crud.config.enabled_oauth_providers.map((p) => ({
          id: p.id
        }))
      }
    };
  }
  _clientPermissionFromCrud(crud) {
    return {
      id: crud.id
    };
  }
  _clientTeamUserFromCrud(crud) {
    return {
      id: crud.user_id,
      teamProfile: {
        displayName: crud.display_name,
        profileImageUrl: crud.profile_image_url
      }
    };
  }
  _clientTeamInvitationFromCrud(session, crud) {
    return {
      id: crud.id,
      recipientEmail: crud.recipient_email,
      expiresAt: new Date(crud.expires_at_millis),
      revoke: async () => {
        await this._interface.revokeTeamInvitation(crud.id, crud.team_id, session);
        await this._teamInvitationsCache.refresh([session, crud.team_id]);
      }
    };
  }
  _baseApiKeyFromCrud(crud) {
    return {
      id: crud.id,
      description: crud.description,
      expiresAt: crud.expires_at_millis ? new Date(crud.expires_at_millis) : void 0,
      manuallyRevokedAt: crud.manually_revoked_at_millis ? new Date(crud.manually_revoked_at_millis) : null,
      createdAt: new Date(crud.created_at_millis),
      ...crud.type === "team" ? { type: "team", teamId: crud.team_id } : { type: "user", userId: crud.user_id },
      value: typeof crud.value === "string" ? crud.value : {
        lastFour: crud.value.last_four
      },
      isValid: function() {
        return this.whyInvalid() === null;
      },
      whyInvalid: function() {
        if (this.manuallyRevokedAt) {
          return "manually-revoked";
        }
        if (this.expiresAt && this.expiresAt < /* @__PURE__ */ new Date()) {
          return "expired";
        }
        return null;
      }
    };
  }
  _clientApiKeyFromCrud(session, crud) {
    return {
      ...this._baseApiKeyFromCrud(crud),
      async revoke() {
        await this.update({ revoked: true });
      },
      update: async (options) => {
        await this._interface.updateProjectApiKey(crud.type === "team" ? { team_id: crud.team_id } : { user_id: crud.user_id }, crud.id, options, session, "client");
        if (crud.type === "team") {
          await this._teamApiKeysCache.refresh([session, crud.team_id]);
        } else {
          await this._userApiKeysCache.refresh([session]);
        }
      }
    };
  }
  _clientTeamFromCrud(crud, session) {
    const app = this;
    return {
      id: crud.id,
      displayName: crud.display_name,
      profileImageUrl: crud.profile_image_url,
      clientMetadata: crud.client_metadata,
      clientReadOnlyMetadata: crud.client_read_only_metadata,
      ...this._createCustomer(crud.id, "team", session),
      async inviteUser(options) {
        await app._interface.sendTeamInvitation({
          teamId: crud.id,
          email: options.email,
          session,
          callbackUrl: options.callbackUrl ?? constructRedirectUrl(app.urls.teamInvitation, "callbackUrl")
        });
        await app._teamInvitationsCache.refresh([session, crud.id]);
      },
      async listUsers() {
        const result = Result.orThrow(await app._teamMemberProfilesCache.getOrWait([session, crud.id], "write-only"));
        return result.map((crud2) => app._clientTeamUserFromCrud(crud2));
      },
      useUsers() {
        const result = useAsyncCache(app._teamMemberProfilesCache, [session, crud.id], "team.useUsers()");
        return result.map((crud2) => app._clientTeamUserFromCrud(crud2));
      },
      async listInvitations() {
        const result = Result.orThrow(await app._teamInvitationsCache.getOrWait([session, crud.id], "write-only"));
        return result.map((crud2) => app._clientTeamInvitationFromCrud(session, crud2));
      },
      useInvitations() {
        const result = useAsyncCache(app._teamInvitationsCache, [session, crud.id], "team.useInvitations()");
        return result.map((crud2) => app._clientTeamInvitationFromCrud(session, crud2));
      },
      async update(data) {
        await app._interface.updateTeam({ data: teamUpdateOptionsToCrud(data), teamId: crud.id }, session);
        await app._currentUserTeamsCache.refresh([session]);
      },
      async delete() {
        await app._interface.deleteTeam(crud.id, session);
        await app._currentUserTeamsCache.refresh([session]);
      },
      useApiKeys() {
        const result = useAsyncCache(app._teamApiKeysCache, [session, crud.id], "team.useApiKeys()");
        return result.map((crud2) => app._clientApiKeyFromCrud(session, crud2));
      },
      async listApiKeys() {
        const results = Result.orThrow(await app._teamApiKeysCache.getOrWait([session, crud.id], "write-only"));
        return results.map((crud2) => app._clientApiKeyFromCrud(session, crud2));
      },
      async createApiKey(options) {
        const result = await app._interface.createProjectApiKey(
          await apiKeyCreationOptionsToCrud("team", crud.id, options),
          session,
          "client"
        );
        await app._teamApiKeysCache.refresh([session, crud.id]);
        return app._clientApiKeyFromCrud(session, result);
      }
    };
  }
  _clientContactChannelFromCrud(crud, session) {
    const app = this;
    return {
      id: crud.id,
      value: crud.value,
      type: crud.type,
      isVerified: crud.is_verified,
      isPrimary: crud.is_primary,
      usedForAuth: crud.used_for_auth,
      async sendVerificationEmail(options) {
        await app._interface.sendCurrentUserContactChannelVerificationEmail(
          crud.id,
          options?.callbackUrl || constructRedirectUrl(app.urls.emailVerification, "callbackUrl"),
          session
        );
      },
      async update(data) {
        await app._interface.updateClientContactChannel(crud.id, contactChannelUpdateOptionsToCrud(data), session);
        await app._clientContactChannelsCache.refresh([session]);
      },
      async delete() {
        await app._interface.deleteClientContactChannel(crud.id, session);
        await app._clientContactChannelsCache.refresh([session]);
      }
    };
  }
  _clientNotificationCategoryFromCrud(crud, session) {
    const app = this;
    return {
      id: crud.notification_category_id,
      name: crud.notification_category_name,
      enabled: crud.enabled,
      canDisable: crud.can_disable,
      async setEnabled(enabled) {
        await app._interface.setNotificationsEnabled(crud.notification_category_id, enabled, session);
        await app._notificationCategoriesCache.refresh([session]);
      }
    };
  }
  _clientOAuthProviderFromCrud(crud, session) {
    const app = this;
    return {
      id: crud.id,
      type: crud.type,
      userId: crud.user_id,
      email: crud.email,
      allowSignIn: crud.allow_sign_in,
      allowConnectedAccounts: crud.allow_connected_accounts,
      async update(data) {
        try {
          await app._interface.updateOAuthProvider(
            crud.user_id,
            crud.id,
            {
              allow_sign_in: data.allowSignIn,
              allow_connected_accounts: data.allowConnectedAccounts
            },
            session
          );
          await app._currentUserOAuthProvidersCache.refresh([session]);
          return Result.ok(void 0);
        } catch (error) {
          if (KnownErrors.OAuthProviderAccountIdAlreadyUsedForSignIn.isInstance(error)) {
            return Result.error(error);
          }
          throw error;
        }
      },
      async delete() {
        await app._interface.deleteOAuthProvider(crud.user_id, crud.id, session);
        await app._currentUserOAuthProvidersCache.refresh([session]);
      }
    };
  }
  _clientItemFromCrud(crud) {
    const app = this;
    return {
      displayName: crud.display_name,
      quantity: crud.quantity,
      nonNegativeQuantity: Math.max(0, crud.quantity)
    };
  }
  _customerProductsFromResponse(response) {
    const products = response.items.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      displayName: item.product.display_name,
      customerType: item.product.customer_type,
      isServerOnly: item.product.server_only,
      stackable: item.product.stackable,
      type: item.type,
      subscription: item.subscription ? {
        currentPeriodEnd: item.subscription.current_period_end ? new Date(item.subscription.current_period_end) : null,
        cancelAtPeriodEnd: item.subscription.cancel_at_period_end,
        isCancelable: item.subscription.is_cancelable
      } : null,
      switchOptions: item.switch_options?.map((option) => ({
        productId: option.product_id,
        displayName: option.product.display_name,
        prices: option.product.prices
      }))
    }));
    return Object.assign(products, { nextCursor: response.pagination.next_cursor ?? null });
  }
  _customerInvoicesFromResponse(response) {
    const invoices = response.items.map((item) => ({
      status: item.status,
      amountTotal: item.amount_total,
      hostedInvoiceUrl: item.hosted_invoice_url,
      createdAt: new Date(item.created_at_millis)
    }));
    return Object.assign(invoices, { nextCursor: response.pagination.next_cursor ?? null });
  }
  _customerBillingFromResponse(response) {
    return {
      hasCustomer: response.has_customer,
      defaultPaymentMethod: response.default_payment_method
    };
  }
  _createAuth(session) {
    const app = this;
    return {
      _internalSession: session,
      currentSession: {
        async getTokens() {
          const tokens = await session.getOrFetchLikelyValidTokens(2e4, 75e3);
          return {
            accessToken: tokens?.accessToken.token ?? null,
            refreshToken: tokens?.refreshToken?.token ?? null
          };
        },
        useTokens() {
          const [_, setCounter] = React.useState(0);
          React.useEffect(() => {
            const { unsubscribe: unsubscribeRefresh } = session.startRefreshingAccessToken(3e4, 6e4);
            const { unsubscribe: unsubscribeInvalidate } = session.onInvalidate(() => setCounter((c) => c + 1));
            const { unsubscribe: unsubscribeAccessTokenChange } = session.onAccessTokenChange(() => setCounter((c) => c + 1));
            return () => {
              unsubscribeRefresh();
              unsubscribeInvalidate();
              unsubscribeAccessTokenChange();
            };
          }, []);
          let accessToken = session.isKnownToBeInvalid() ? null : session.getAccessTokenIfNotExpiredYet(2e4, 75e3);
          if (accessToken === null) {
            accessToken = use(session.getOrFetchLikelyValidTokens(2e4, 75e3))?.accessToken ?? null;
          }
          return {
            accessToken: accessToken?.token ?? null,
            refreshToken: session.getRefreshToken()?.token ?? null
          };
        }
      },
      async getAccessToken() {
        const tokens = await this.currentSession.getTokens();
        return tokens.accessToken;
      },
      useAccessToken() {
        return this.currentSession.useTokens().accessToken;
      },
      async getRefreshToken() {
        const tokens = await this.currentSession.getTokens();
        return tokens.refreshToken;
      },
      useRefreshToken() {
        return this.currentSession.useTokens().refreshToken;
      },
      async getAuthHeaders() {
        return {
          "x-stack-auth": JSON.stringify(await this.getAuthJson())
        };
      },
      useAuthHeaders() {
        return {
          "x-stack-auth": JSON.stringify(this.useAuthJson())
        };
      },
      async getAuthJson() {
        const tokens = await this.currentSession.getTokens();
        return tokens;
      },
      useAuthJson() {
        return this.currentSession.useTokens();
      },
      signOut(options) {
        return app._signOut(session, options);
      }
    };
  }
  _editableTeamProfileFromCrud(crud, session) {
    const app = this;
    return {
      displayName: crud.display_name,
      profileImageUrl: crud.profile_image_url,
      async update(update) {
        await app._interface.updateTeamMemberProfile({
          teamId: crud.team_id,
          userId: crud.user_id,
          profile: {
            display_name: update.displayName,
            profile_image_url: update.profileImageUrl
          }
        }, session);
        await app._currentUserTeamProfileCache.refresh([session, crud.team_id]);
      }
    };
  }
  _createBaseUser(crud) {
    return {
      id: crud.id,
      displayName: crud.display_name,
      primaryEmail: crud.primary_email,
      primaryEmailVerified: crud.primary_email_verified,
      profileImageUrl: crud.profile_image_url,
      signedUpAt: new Date(crud.signed_up_at_millis),
      clientMetadata: crud.client_metadata,
      clientReadOnlyMetadata: crud.client_read_only_metadata,
      hasPassword: crud.has_password,
      emailAuthEnabled: crud.auth_with_email,
      otpAuthEnabled: crud.otp_auth_enabled,
      oauthProviders: crud.oauth_providers,
      passkeyAuthEnabled: crud.passkey_auth_enabled,
      isMultiFactorRequired: crud.requires_totp_mfa,
      isAnonymous: crud.is_anonymous,
      isRestricted: crud.is_restricted,
      restrictedReason: crud.restricted_reason,
      toClientJson() {
        return crud;
      }
    };
  }
  _createUserExtraFromCurrent(crud, session) {
    const app = this;
    async function getConnectedAccount(id, options) {
      const scopeString = options?.scopes?.join(" ");
      return Result.orThrow(await app._currentUserOAuthConnectionCache.getOrWait([session, id, scopeString || "", options?.or === "redirect"], "write-only"));
    }
    function useConnectedAccount(id, options) {
      const scopeString = options?.scopes?.join(" ");
      return useAsyncCache(app._currentUserOAuthConnectionCache, [session, id, scopeString || "", options?.or === "redirect"], "user.useConnectedAccount()");
    }
    return {
      async getActiveSessions() {
        const sessions = await app._interface.listSessions(session);
        return sessions.items.map((crud2) => app._clientSessionFromCrud(crud2));
      },
      async revokeSession(sessionId) {
        await app._interface.deleteSession(sessionId, session);
      },
      setDisplayName(displayName) {
        return this.update({ displayName });
      },
      setClientMetadata(metadata) {
        return this.update({ clientMetadata: metadata });
      },
      async setSelectedTeam(team) {
        await this.update({ selectedTeamId: typeof team === "string" ? team : team?.id ?? null });
      },
      getConnectedAccount,
      useConnectedAccount,
      // THIS_LINE_PLATFORM react-like
      async getTeam(teamId) {
        const teams = await this.listTeams();
        return teams.find((t) => t.id === teamId) ?? null;
      },
      useTeam(teamId) {
        const teams = this.useTeams();
        return useMemo(() => {
          return teams.find((t) => t.id === teamId) ?? null;
        }, [teams, teamId]);
      },
      async listTeams() {
        const teams = Result.orThrow(await app._currentUserTeamsCache.getOrWait([session], "write-only"));
        return teams.map((crud2) => app._clientTeamFromCrud(crud2, session));
      },
      useTeams() {
        const teams = useAsyncCache(app._currentUserTeamsCache, [session], "user.useTeams()");
        return useMemo(() => teams.map((crud2) => app._clientTeamFromCrud(crud2, session)), [teams]);
      },
      async createTeam(data) {
        const crud2 = await app._interface.createClientTeam(teamCreateOptionsToCrud(data, "me"), session);
        await app._currentUserTeamsCache.refresh([session]);
        await this.update({ selectedTeamId: crud2.id });
        return app._clientTeamFromCrud(crud2, session);
      },
      async leaveTeam(team) {
        await app._interface.leaveTeam(team.id, session);
      },
      async listPermissions(scopeOrOptions, options) {
        if (scopeOrOptions && "id" in scopeOrOptions) {
          const scope = scopeOrOptions;
          const recursive = options?.recursive ?? true;
          const permissions = Result.orThrow(await app._currentUserPermissionsCache.getOrWait([session, scope.id, recursive], "write-only"));
          return permissions.map((crud2) => app._clientPermissionFromCrud(crud2));
        } else {
          const opts = scopeOrOptions;
          const recursive = opts?.recursive ?? true;
          const permissions = Result.orThrow(await app._currentUserProjectPermissionsCache.getOrWait([session, recursive], "write-only"));
          return permissions.map((crud2) => app._clientPermissionFromCrud(crud2));
        }
      },
      usePermissions(scopeOrOptions, options) {
        if (scopeOrOptions && "id" in scopeOrOptions) {
          const scope = scopeOrOptions;
          const recursive = options?.recursive ?? true;
          const permissions = useAsyncCache(app._currentUserPermissionsCache, [session, scope.id, recursive], "user.usePermissions()");
          return useMemo(() => permissions.map((crud2) => app._clientPermissionFromCrud(crud2)), [permissions]);
        } else {
          const opts = scopeOrOptions;
          const recursive = opts?.recursive ?? true;
          const permissions = useAsyncCache(app._currentUserProjectPermissionsCache, [session, recursive], "user.usePermissions()");
          return useMemo(() => permissions.map((crud2) => app._clientPermissionFromCrud(crud2)), [permissions]);
        }
      },
      usePermission(scopeOrPermissionId, permissionId) {
        if (scopeOrPermissionId && typeof scopeOrPermissionId !== "string") {
          const scope = scopeOrPermissionId;
          const permissions = this.usePermissions(scope);
          return useMemo(() => permissions.find((p) => p.id === permissionId) ?? null, [permissions, permissionId]);
        } else {
          const pid = scopeOrPermissionId;
          const permissions = this.usePermissions();
          return useMemo(() => permissions.find((p) => p.id === pid) ?? null, [permissions, pid]);
        }
      },
      async getPermission(scopeOrPermissionId, permissionId) {
        if (scopeOrPermissionId && typeof scopeOrPermissionId !== "string") {
          const scope = scopeOrPermissionId;
          const permissions = await this.listPermissions(scope);
          return permissions.find((p) => p.id === permissionId) ?? null;
        } else {
          const pid = scopeOrPermissionId;
          const permissions = await this.listPermissions();
          return permissions.find((p) => p.id === pid) ?? null;
        }
      },
      async hasPermission(scopeOrPermissionId, permissionId) {
        if (scopeOrPermissionId && typeof scopeOrPermissionId !== "string") {
          const scope = scopeOrPermissionId;
          return await this.getPermission(scope, permissionId) !== null;
        } else {
          const pid = scopeOrPermissionId;
          return await this.getPermission(pid) !== null;
        }
      },
      async update(update) {
        return await app._updateClientUser(update, session);
      },
      async sendVerificationEmail(options) {
        if (!crud.primary_email) {
          throw new StackAssertionError("User does not have a primary email");
        }
        return await app._interface.sendVerificationEmail(
          crud.primary_email,
          options?.callbackUrl ?? constructRedirectUrl(app.urls.emailVerification, "callbackUrl"),
          session
        );
      },
      async updatePassword(options) {
        const result = await app._interface.updatePassword(options, session);
        await app._currentUserCache.refresh([session]);
        return result;
      },
      async setPassword(options) {
        const result = await app._interface.setPassword(options, session);
        await app._currentUserCache.refresh([session]);
        return result;
      },
      selectedTeam: crud.selected_team && this._clientTeamFromCrud(crud.selected_team, session),
      async getTeamProfile(team) {
        const result = Result.orThrow(await app._currentUserTeamProfileCache.getOrWait([session, team.id], "write-only"));
        return app._editableTeamProfileFromCrud(result, session);
      },
      useTeamProfile(team) {
        const result = useAsyncCache(app._currentUserTeamProfileCache, [session, team.id], "user.useTeamProfile()");
        return app._editableTeamProfileFromCrud(result, session);
      },
      async delete() {
        await app._interface.deleteCurrentUser(session);
        session.markInvalid();
      },
      async listContactChannels() {
        const result = Result.orThrow(await app._clientContactChannelsCache.getOrWait([session], "write-only"));
        return result.map((crud2) => app._clientContactChannelFromCrud(crud2, session));
      },
      useContactChannels() {
        const result = useAsyncCache(app._clientContactChannelsCache, [session], "user.useContactChannels()");
        return result.map((crud2) => app._clientContactChannelFromCrud(crud2, session));
      },
      async createContactChannel(data) {
        const crud2 = await app._interface.createClientContactChannel(contactChannelCreateOptionsToCrud("me", data), session);
        await app._clientContactChannelsCache.refresh([session]);
        return app._clientContactChannelFromCrud(crud2, session);
      },
      useNotificationCategories() {
        const results = useAsyncCache(app._notificationCategoriesCache, [session], "user.useNotificationCategories()");
        return results.map((crud2) => app._clientNotificationCategoryFromCrud(crud2, session));
      },
      async listNotificationCategories() {
        const results = Result.orThrow(await app._notificationCategoriesCache.getOrWait([session], "write-only"));
        return results.map((crud2) => app._clientNotificationCategoryFromCrud(crud2, session));
      },
      useApiKeys() {
        const result = useAsyncCache(app._userApiKeysCache, [session], "user.useApiKeys()");
        return result.map((crud2) => app._clientApiKeyFromCrud(session, crud2));
      },
      async listApiKeys() {
        const results = await app._interface.listProjectApiKeys({ user_id: "me" }, session, "client");
        return results.map((crud2) => app._clientApiKeyFromCrud(session, crud2));
      },
      async createApiKey(options) {
        const result = await app._interface.createProjectApiKey(
          await apiKeyCreationOptionsToCrud("user", "me", options),
          session,
          "client"
        );
        await app._userApiKeysCache.refresh([session]);
        return app._clientApiKeyFromCrud(session, result);
      },
      useOAuthProviders() {
        const results = useAsyncCache(app._currentUserOAuthProvidersCache, [session], "user.useOAuthProviders()");
        return results.map((crud2) => app._clientOAuthProviderFromCrud(crud2, session));
      },
      async listOAuthProviders() {
        const results = Result.orThrow(await app._currentUserOAuthProvidersCache.getOrWait([session], "write-only"));
        return results.map((crud2) => app._clientOAuthProviderFromCrud(crud2, session));
      },
      useOAuthProvider(id) {
        const providers = this.useOAuthProviders();
        return useMemo(() => providers.find((p) => p.id === id) ?? null, [providers, id]);
      },
      async getOAuthProvider(id) {
        const providers = await this.listOAuthProviders();
        return providers.find((p) => p.id === id) ?? null;
      },
      async registerPasskey(options) {
        const hostname = (await app._getCurrentUrl())?.hostname;
        if (!hostname) {
          throw new StackAssertionError("hostname must be provided if the Stack App does not have a redirect method");
        }
        const initiationResult = await app._interface.initiatePasskeyRegistration({}, session);
        if (initiationResult.status !== "ok") {
          return Result.error(new KnownErrors.PasskeyRegistrationFailed("Failed to get initiation options for passkey registration"));
        }
        const { options_json, code } = initiationResult.data;
        if (options_json.rp.id !== "THIS_VALUE_WILL_BE_REPLACED.example.com") {
          throw new StackAssertionError(`Expected returned RP ID from server to equal sentinel, but found ${options_json.rp.id}`);
        }
        options_json.rp.id = hostname;
        let attResp;
        try {
          attResp = await startRegistration({ optionsJSON: options_json });
        } catch (error) {
          if (error instanceof WebAuthnError) {
            return Result.error(new KnownErrors.PasskeyWebAuthnError(error.message, error.name));
          } else {
            captureError("passkey-registration-failed", error);
            return Result.error(new KnownErrors.PasskeyRegistrationFailed("Failed to start passkey registration due to unknown error"));
          }
        }
        const registrationResult = await app._interface.registerPasskey({ credential: attResp, code }, session);
        await app._refreshUser(session);
        return registrationResult;
      }
    };
  }
  _createInternalUserExtra(session) {
    const app = this;
    this._ensureInternalProject();
    return {
      createProject(newProject) {
        return app._createProject(session, newProject);
      },
      async transferProject(projectIdToTransfer, newTeamId) {
        await app._interface.transferProject(session, projectIdToTransfer, newTeamId);
        await app._refreshProject();
      },
      listOwnedProjects() {
        return app._listOwnedProjects(session);
      },
      useOwnedProjects() {
        return app._useOwnedProjects(session);
      }
    };
  }
  _createCustomer(userIdOrTeamId, type, session) {
    const app = this;
    const effectiveSession = session ?? app._interface.createSession({ refreshToken: null });
    const customerOptions = type === "user" ? { userId: userIdOrTeamId } : { teamId: userIdOrTeamId };
    return {
      async getBilling() {
        const response = Result.orThrow(await app._customerBillingCache.getOrWait([effectiveSession, type, userIdOrTeamId], "write-only"));
        return app._customerBillingFromResponse(response);
      },
      useBilling() {
        const response = useAsyncCache(app._customerBillingCache, [effectiveSession, type, userIdOrTeamId], "customer.useBilling()");
        return app._customerBillingFromResponse(response);
      },
      async createPaymentMethodSetupIntent() {
        const body = await app._interface.createCustomerPaymentMethodSetupIntent(type, userIdOrTeamId, effectiveSession);
        return {
          clientSecret: body.client_secret,
          stripeAccountId: body.stripe_account_id
        };
      },
      async setDefaultPaymentMethodFromSetupIntent(setupIntentId) {
        const body = await app._interface.setDefaultCustomerPaymentMethodFromSetupIntent(type, userIdOrTeamId, setupIntentId, effectiveSession);
        await app._customerBillingCache.refresh([effectiveSession, type, userIdOrTeamId]);
        return body.default_payment_method;
      },
      async getItem(itemId) {
        return await app.getItem({ itemId, ...customerOptions });
      },
      useItem(itemId) {
        return app.useItem({ itemId, ...customerOptions });
      },
      async listProducts(options) {
        return await app.listProducts({ ...options, ...customerOptions });
      },
      useProducts(options) {
        return app.useProducts({ ...options, ...customerOptions });
      },
      async listInvoices(options) {
        return await app.listInvoices({ ...options, ...customerOptions });
      },
      useInvoices(options) {
        return app.useInvoices({ ...options, ...customerOptions });
      },
      async createCheckoutUrl(options) {
        return await app._interface.createCheckoutUrl(type, userIdOrTeamId, options.productId, effectiveSession, options.returnUrl);
      },
      async switchSubscription(options) {
        await app._interface.switchSubscription({
          customer_type: type,
          customer_id: userIdOrTeamId,
          from_product_id: options.fromProductId,
          to_product_id: options.toProductId,
          price_id: options.priceId,
          quantity: options.quantity
        }, effectiveSession);
        await app._customerBillingCache.refresh([effectiveSession, type, userIdOrTeamId]);
        if (type === "user") {
          await app._userProductsCache.invalidateWhere(([cachedSession, userId]) => cachedSession === effectiveSession && userId === userIdOrTeamId);
        } else {
          await app._teamProductsCache.invalidateWhere(([cachedSession, teamId]) => cachedSession === effectiveSession && teamId === userIdOrTeamId);
        }
      }
    };
  }
  async getItem(options) {
    const session = await this._getSession();
    let crud;
    if ("userId" in options) {
      crud = Result.orThrow(await this._userItemCache.getOrWait([session, options.userId, options.itemId], "write-only"));
    } else if ("teamId" in options) {
      crud = Result.orThrow(await this._teamItemCache.getOrWait([session, options.teamId, options.itemId], "write-only"));
    } else {
      crud = Result.orThrow(await this._customItemCache.getOrWait([session, options.customCustomerId, options.itemId], "write-only"));
    }
    return this._clientItemFromCrud(crud);
  }
  useItem(options) {
    const session = this._useSession();
    const [cache, ownerId] = "userId" in options ? [this._userItemCache, options.userId] : "teamId" in options ? [this._teamItemCache, options.teamId] : [this._customItemCache, options.customCustomerId];
    const crud = useAsyncCache(cache, [session, ownerId, options.itemId], "app.useItem()");
    return this._clientItemFromCrud(crud);
  }
  async listProducts(options) {
    const session = await this._getSession();
    if ("userId" in options) {
      const response2 = Result.orThrow(await this._userProductsCache.getOrWait([session, options.userId, options.cursor ?? null, options.limit ?? null], "write-only"));
      return this._customerProductsFromResponse(response2);
    } else if ("teamId" in options) {
      const response2 = Result.orThrow(await this._teamProductsCache.getOrWait([session, options.teamId, options.cursor ?? null, options.limit ?? null], "write-only"));
      return this._customerProductsFromResponse(response2);
    }
    const response = Result.orThrow(await this._customProductsCache.getOrWait([session, options.customCustomerId, options.cursor ?? null, options.limit ?? null], "write-only"));
    return this._customerProductsFromResponse(response);
  }
  async listInvoices(options) {
    const session = await this._getSession();
    if ("userId" in options) {
      const response2 = Result.orThrow(await this._userInvoicesCache.getOrWait([session, options.userId, options.cursor ?? null, options.limit ?? null], "write-only"));
      return this._customerInvoicesFromResponse(response2);
    }
    const response = Result.orThrow(await this._teamInvoicesCache.getOrWait([session, options.teamId, options.cursor ?? null, options.limit ?? null], "write-only"));
    return this._customerInvoicesFromResponse(response);
  }
  async cancelSubscription(options) {
    const session = await this._getSession();
    const user = await this.getUser();
    if (!user) {
      throw new KnownErrors.UserAuthenticationRequired();
    }
    const customerType = "teamId" in options ? "team" : "user";
    const customerId = "teamId" in options ? options.teamId : user.id;
    await this._interface.cancelSubscription({
      customer_type: customerType,
      customer_id: customerId,
      product_id: options.productId
    }, session);
    if (customerType === "user") {
      await this._userProductsCache.invalidateWhere(([cachedSession, userId]) => cachedSession === session && userId === customerId);
    } else {
      await this._teamProductsCache.invalidateWhere(([cachedSession, teamId]) => cachedSession === session && teamId === customerId);
    }
  }
  useProducts(options) {
    const session = this._useSession();
    const cache = "userId" in options ? this._userProductsCache : "teamId" in options ? this._teamProductsCache : this._customProductsCache;
    const debugLabel = "clientApp.useProducts()";
    const customerId = "userId" in options ? options.userId : "teamId" in options ? options.teamId : options.customCustomerId;
    const response = useAsyncCache(cache, [session, customerId, options.cursor ?? null, options.limit ?? null], debugLabel);
    return this._customerProductsFromResponse(response);
  }
  useInvoices(options) {
    const session = this._useSession();
    const cache = "userId" in options ? this._userInvoicesCache : this._teamInvoicesCache;
    const debugLabel = "clientApp.useInvoices()";
    const customerId = "userId" in options ? options.userId : options.teamId;
    const response = useAsyncCache(cache, [session, customerId, options.cursor ?? null, options.limit ?? null], debugLabel);
    return this._customerInvoicesFromResponse(response);
  }
  _currentUserFromCrud(crud, session) {
    const currentUser = withUserDestructureGuard({
      ...this._createBaseUser(crud),
      ...this._createAuth(session),
      ...this._createUserExtraFromCurrent(crud, session),
      ...this._isInternalProject() ? this._createInternalUserExtra(session) : {},
      ...this._createCustomer(crud.id, "user", session)
    });
    return currentUser;
  }
  _clientSessionFromCrud(crud) {
    return {
      id: crud.id,
      userId: crud.user_id,
      createdAt: new Date(crud.created_at),
      isImpersonation: crud.is_impersonation,
      lastUsedAt: crud.last_used_at ? new Date(crud.last_used_at) : void 0,
      isCurrentSession: crud.is_current_session ?? false,
      geoInfo: crud.last_used_at_end_user_ip_info
    };
  }
  _getOwnedAdminApp(forProjectId, session) {
    if (!this._ownedAdminApps.has([session, forProjectId])) {
      this._ownedAdminApps.set([session, forProjectId], new __StackClientAppImplIncomplete.LazyStackAdminAppImpl.value({
        baseUrl: this._interface.options.getBaseUrl(),
        projectId: forProjectId,
        tokenStore: null,
        projectOwnerSession: session,
        noAutomaticPrefetch: true
      }));
    }
    return this._ownedAdminApps.get([session, forProjectId]);
  }
  get projectId() {
    return this._interface.projectId;
  }
  async _isTrusted(url) {
    return isRelative(url);
  }
  get urls() {
    return getUrls(this._urlOptions);
  }
  async _getCurrentUrl() {
    if (this._redirectMethod === "none") {
      return null;
    }
    return new URL(window.location.href);
  }
  async _redirectTo(options) {
    if (this._redirectMethod === "none") {
      return;
    } else if (isReactServer2 && this._redirectMethod === "nextjs") {
      NextNavigation.redirect(options.url.toString(), options.replace ? NextNavigation.RedirectType.replace : NextNavigation.RedirectType.push);
    } else if (typeof this._redirectMethod === "object" && this._redirectMethod.navigate) {
      this._redirectMethod.navigate(options.url.toString());
    } else {
      if (options.replace) {
        window.location.replace(options.url);
      } else {
        window.location.assign(options.url);
      }
    }
    await wait(2e3);
  }
  useNavigate() {
    if (typeof this._redirectMethod === "object") {
      return this._redirectMethod.useNavigate();
    } else if (this._redirectMethod === "window") {
      return (to) => window.location.assign(to);
    } else if (this._redirectMethod === "nextjs") {
      const router = NextNavigation.useRouter();
      return (to) => router.push(to);
    } else {
      return (to) => {
      };
    }
  }
  async _redirectIfTrusted(url, options) {
    if (!await this._isTrusted(url)) {
      throw new Error(`Redirect URL ${url} is not trusted; should be relative.`);
    }
    return await this._redirectTo({ url, ...options });
  }
  async _redirectToHandler(handlerName, options) {
    let url = this.urls[handlerName];
    if (!url) {
      throw new Error(`No URL for handler name ${handlerName}`);
    }
    if (!options?.noRedirectBack) {
      if (handlerName === "afterSignIn" || handlerName === "afterSignUp") {
        if (isReactServer2 || typeof window === "undefined") {
        } else {
          const queryParams = new URLSearchParams(window.location.search);
          url = queryParams.get("after_auth_return_to") || url;
        }
      } else if (handlerName === "signIn" || handlerName === "signUp" || handlerName === "onboarding") {
        if (isReactServer2 || typeof window === "undefined") {
        } else {
          const currentUrl = new URL(window.location.href);
          const nextUrl = new URL(url, currentUrl);
          if (currentUrl.searchParams.has("after_auth_return_to")) {
            nextUrl.searchParams.set("after_auth_return_to", currentUrl.searchParams.get("after_auth_return_to"));
          } else if (currentUrl.protocol === nextUrl.protocol && currentUrl.host === nextUrl.host) {
            nextUrl.searchParams.set("after_auth_return_to", getRelativePart(currentUrl));
          }
          url = getRelativePart(nextUrl);
        }
      }
    }
    await this._redirectIfTrusted(url, options);
  }
  async redirectToSignIn(options) {
    return await this._redirectToHandler("signIn", options);
  }
  async redirectToSignUp(options) {
    return await this._redirectToHandler("signUp", options);
  }
  async redirectToSignOut(options) {
    return await this._redirectToHandler("signOut", options);
  }
  async redirectToEmailVerification(options) {
    return await this._redirectToHandler("emailVerification", options);
  }
  async redirectToPasswordReset(options) {
    return await this._redirectToHandler("passwordReset", options);
  }
  async redirectToForgotPassword(options) {
    return await this._redirectToHandler("forgotPassword", options);
  }
  async redirectToHome(options) {
    return await this._redirectToHandler("home", options);
  }
  async redirectToOAuthCallback(options) {
    return await this._redirectToHandler("oauthCallback", options);
  }
  async redirectToMagicLinkCallback(options) {
    return await this._redirectToHandler("magicLinkCallback", options);
  }
  async redirectToAfterSignIn(options) {
    return await this._redirectToHandler("afterSignIn", options);
  }
  async redirectToAfterSignUp(options) {
    return await this._redirectToHandler("afterSignUp", options);
  }
  async redirectToOnboarding(options) {
    return await this._redirectToHandler("onboarding", options);
  }
  async redirectToAfterSignOut(options) {
    return await this._redirectToHandler("afterSignOut", options);
  }
  async redirectToAccountSettings(options) {
    return await this._redirectToHandler("accountSettings", options);
  }
  async redirectToError(options) {
    return await this._redirectToHandler("error", options);
  }
  async redirectToTeamInvitation(options) {
    return await this._redirectToHandler("teamInvitation", options);
  }
  async redirectToMfa(options) {
    return await this._redirectToHandler("mfa", options);
  }
  async sendForgotPasswordEmail(email, options) {
    return await this._interface.sendForgotPasswordEmail(email, options?.callbackUrl ?? constructRedirectUrl(this.urls.passwordReset, "callbackUrl"));
  }
  async sendMagicLinkEmail(email, options) {
    return await this._interface.sendMagicLinkEmail(email, options?.callbackUrl ?? constructRedirectUrl(this.urls.magicLinkCallback, "callbackUrl"));
  }
  async resetPassword(options) {
    return await this._interface.resetPassword(options);
  }
  async verifyPasswordResetCode(code) {
    return await this._interface.verifyPasswordResetCode(code);
  }
  async verifyTeamInvitationCode(code) {
    return await this._interface.acceptTeamInvitation({
      type: "check",
      code,
      session: await this._getSession()
    });
  }
  async acceptTeamInvitation(code) {
    const result = await this._interface.acceptTeamInvitation({
      type: "use",
      code,
      session: await this._getSession()
    });
    if (result.status === "ok") {
      return Result.ok(void 0);
    } else {
      return Result.error(result.error);
    }
  }
  async getTeamInvitationDetails(code) {
    const result = await this._interface.acceptTeamInvitation({
      type: "details",
      code,
      session: await this._getSession()
    });
    if (result.status === "ok") {
      return Result.ok({ teamDisplayName: result.data.team_display_name });
    } else {
      return Result.error(result.error);
    }
  }
  async verifyEmail(code) {
    const result = await this._interface.verifyEmail(code);
    await this._currentUserCache.refresh([await this._getSession()]);
    await this._clientContactChannelsCache.refresh([await this._getSession()]);
    return result;
  }
  async getUser(options) {
    if (options?.or === "anonymous" && options.includeRestricted === false) {
      throw new Error("Cannot use { or: 'anonymous' } with { includeRestricted: false }. Anonymous users implicitly include restricted users.");
    }
    this._ensurePersistentTokenStore(options?.tokenStore);
    const session = await this._getSession(options?.tokenStore);
    let crud = Result.orThrow(await this._currentUserCache.getOrWait([session], "write-only"));
    const includeAnonymous = options?.or === "anonymous" || options?.or === "anonymous-if-exists[deprecated]";
    const includeRestricted = options?.includeRestricted === true || includeAnonymous;
    if (crud === null || crud.is_anonymous && !includeAnonymous || crud.is_restricted && !includeRestricted) {
      switch (options?.or) {
        case "redirect": {
          if (!crud?.is_anonymous && crud?.is_restricted) {
            await this.redirectToOnboarding({ replace: true });
          } else {
            await this.redirectToSignIn({ replace: true });
          }
          break;
        }
        case "throw": {
          throw new Error("User is not signed in but getUser was called with { or: 'throw' }");
        }
        case "anonymous": {
          const tokens = await this._signUpAnonymously();
          return await this.getUser({ tokenStore: tokens, or: "anonymous-if-exists[deprecated]", includeRestricted: true }) ?? throwErr("Something went wrong while signing up anonymously");
        }
        case void 0:
        case "anonymous-if-exists[deprecated]":
        case "return-null": {
          return null;
        }
      }
    }
    return crud && this._currentUserFromCrud(crud, session);
  }
  useUser(options) {
    if (options?.or === "anonymous" && options.includeRestricted === false) {
      throw new Error("Cannot use { or: 'anonymous' } with { includeRestricted: false }. Anonymous users implicitly include restricted users.");
    }
    this._ensurePersistentTokenStore(options?.tokenStore);
    const session = this._useSession(options?.tokenStore);
    let crud = useAsyncCache(this._currentUserCache, [session], "clientApp.useUser()");
    const includeAnonymous = options?.or === "anonymous" || options?.or === "anonymous-if-exists[deprecated]";
    const includeRestricted = options?.includeRestricted === true || includeAnonymous;
    if (crud === null || crud.is_anonymous && !includeAnonymous || crud.is_restricted && !includeRestricted) {
      switch (options?.or) {
        case "redirect": {
          if (!crud?.is_anonymous && crud?.is_restricted) {
            runAsynchronously(this.redirectToOnboarding({ replace: true }));
          } else {
            runAsynchronously(this.redirectToSignIn({ replace: true }));
          }
          suspend();
          throw new StackAssertionError("suspend should never return");
        }
        case "throw": {
          throw new Error("User is not signed in but useUser was called with { or: 'throw' }");
        }
        case "anonymous": {
          runAsynchronously(async () => {
            await this._signUpAnonymously();
            if (typeof window !== "undefined") {
              window.location.reload();
            }
          });
          suspend();
          throw new StackAssertionError("suspend should never return");
        }
        case void 0:
        case "anonymous-if-exists[deprecated]":
        case "return-null": {
        }
      }
    }
    return useMemo(() => {
      return crud && this._currentUserFromCrud(crud, session);
    }, [crud, session, options?.or]);
  }
  _getTokenPartialUserFromSession(session, options) {
    const accessToken = session.getAccessTokenIfNotExpiredYet(0, null);
    if (!accessToken) {
      return null;
    }
    const isAnonymous = accessToken.payload.is_anonymous;
    if (isAnonymous && options.or !== "anonymous") {
      return null;
    }
    return {
      id: accessToken.payload.sub,
      primaryEmail: accessToken.payload.email,
      displayName: accessToken.payload.name,
      primaryEmailVerified: accessToken.payload.email_verified,
      isAnonymous,
      isRestricted: accessToken.payload.is_restricted,
      restrictedReason: accessToken.payload.restricted_reason
    };
  }
  async _getPartialUserFromConvex(ctx) {
    const auth = await ctx.auth.getUserIdentity();
    if (!auth) {
      return null;
    }
    return {
      id: auth.subject,
      displayName: auth.name ?? null,
      primaryEmail: auth.email ?? null,
      primaryEmailVerified: auth.email_verified,
      isAnonymous: auth.is_anonymous,
      isRestricted: auth.is_restricted,
      restrictedReason: auth.restricted_reason ?? null
    };
  }
  async getPartialUser(options) {
    switch (options.from) {
      case "token": {
        this._ensurePersistentTokenStore(options.tokenStore ?? this._tokenStoreInit);
        const session = await this._getSession(options.tokenStore);
        return this._getTokenPartialUserFromSession(session, options);
      }
      case "convex": {
        return await this._getPartialUserFromConvex(options.ctx);
      }
      default: {
        throw new Error(`Invalid 'from' option: ${options.from}`);
      }
    }
  }
  usePartialUser(options) {
    switch (options.from) {
      case "token": {
        this._ensurePersistentTokenStore(options.tokenStore ?? this._tokenStoreInit);
        const session = this._useSession(options.tokenStore);
        return this._getTokenPartialUserFromSession(session, options);
      }
      case "convex": {
        const result = useAsyncCache(this._convexPartialUserCache, [options.ctx], "clientApp.usePartialUser()");
        return result;
      }
      default: {
        throw new Error(`Invalid 'from' option: ${options.from}`);
      }
    }
  }
  getConvexClientAuth(options) {
    return async (args) => {
      const session = await this._getSession(options.tokenStore ?? this._tokenStoreInit);
      if (!args.forceRefreshToken) {
        const tokens2 = await session.getOrFetchLikelyValidTokens(2e4, 75e3);
        return tokens2?.accessToken.token ?? null;
      }
      const tokens = await session.fetchNewTokens();
      return tokens?.accessToken.token ?? null;
    };
  }
  async getConvexHttpClientAuth(options) {
    const session = await this._getSession(options.tokenStore);
    const tokens = await session.getOrFetchLikelyValidTokens(2e4, 75e3);
    return tokens?.accessToken.token ?? "";
  }
  async _updateClientUser(update, session) {
    const res = await this._interface.updateClientUser(userUpdateOptionsToCrud(update), session);
    await this._refreshUser(session);
    return res;
  }
  async signInWithOAuth(provider, options) {
    if (typeof window === "undefined") {
      throw new Error("signInWithOAuth can currently only be called in a browser environment");
    }
    this._ensurePersistentTokenStore();
    const session = await this._getSession();
    await signInWithOAuth(
      this._interface,
      {
        provider,
        redirectUrl: options?.returnTo ?? this.urls.oauthCallback,
        errorRedirectUrl: this.urls.error,
        providerScope: this._oauthScopesOnSignIn[provider]?.join(" ")
      },
      session
    );
  }
  /**
   * Handles MFA verification by redirecting to the OTP page
   */
  async _experimentalMfa(error, session) {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("stack_mfa_attempt_code", error.details?.attempt_code ?? throwErr("attempt code missing"));
    }
    await this.redirectToMfa();
    throw new StackAssertionError("we should have redirected in redirectToMfa()");
  }
  /**
   * @deprecated
   * TODO remove
   */
  async _catchMfaRequiredError(callback) {
    try {
      return await callback();
    } catch (e) {
      if (KnownErrors.MultiFactorAuthenticationRequired.isInstance(e)) {
        return Result.ok(await this._experimentalMfa(e, await this._getSession()));
      }
      throw e;
    }
  }
  async signInWithCredential(options) {
    this._ensurePersistentTokenStore();
    const session = await this._getSession();
    let result;
    try {
      result = await this._catchMfaRequiredError(async () => {
        return await this._interface.signInWithCredential(options.email, options.password, session);
      });
    } catch (e) {
      if (KnownErrors.InvalidTotpCode.isInstance(e)) {
        return Result.error(e);
      }
      throw e;
    }
    if (result.status === "ok") {
      await this._signInToAccountWithTokens(result.data);
      if (!options.noRedirect) {
        await this.redirectToAfterSignIn({ replace: true });
      }
      return Result.ok(void 0);
    } else {
      return Result.error(result.error);
    }
  }
  async signUpWithCredential(options) {
    if (options.noVerificationCallback && options.verificationCallbackUrl) {
      throw new StackAssertionError("verificationCallbackUrl is not allowed when noVerificationCallback is true");
    }
    this._ensurePersistentTokenStore();
    const session = await this._getSession();
    const emailVerificationRedirectUrl = options.noVerificationCallback ? void 0 : options.verificationCallbackUrl ?? constructRedirectUrl(this.urls.emailVerification, "verificationCallbackUrl");
    let result = await this._interface.signUpWithCredential(
      options.email,
      options.password,
      emailVerificationRedirectUrl,
      session
    );
    if (result.status === "error" && result.error instanceof KnownErrors.RedirectUrlNotWhitelisted && !options.noVerificationCallback && emailVerificationRedirectUrl !== void 0) {
      console.error("Warning: The verification callback URL is not trusted. Proceeding with signup without email verification. Please add your domain to the trusted domains list in your Stack Auth dashboard.", { url: emailVerificationRedirectUrl });
      result = await this._interface.signUpWithCredential(
        options.email,
        options.password,
        void 0,
        // No email verification
        session
      );
    }
    if (result.status === "ok") {
      await this._signInToAccountWithTokens(result.data);
      if (!options.noRedirect) {
        await this.redirectToAfterSignUp({ replace: true });
      }
      return Result.ok(void 0);
    } else {
      return Result.error(result.error);
    }
  }
  async _signUpAnonymously() {
    this._ensurePersistentTokenStore();
    if (!this._anonymousSignUpInProgress) {
      this._anonymousSignUpInProgress = (async () => {
        this._ensurePersistentTokenStore();
        const session = await this._getSession();
        const result = await this._interface.signUpAnonymously(session);
        if (result.status === "ok") {
          await this._signInToAccountWithTokens(result.data);
        } else {
          throw new StackAssertionError("signUpAnonymously() should never return an error");
        }
        this._anonymousSignUpInProgress = null;
        return result.data;
      })();
    }
    return await this._anonymousSignUpInProgress;
  }
  async signInWithMagicLink(code, options) {
    this._ensurePersistentTokenStore();
    const session = await this._getSession();
    let result;
    try {
      result = await this._catchMfaRequiredError(async () => {
        return await this._interface.signInWithMagicLink(code, session);
      });
    } catch (e) {
      if (KnownErrors.InvalidTotpCode.isInstance(e)) {
        return Result.error(e);
      }
      throw e;
    }
    if (result.status === "ok") {
      await this._signInToAccountWithTokens(result.data);
      if (!options?.noRedirect) {
        if (result.data.newUser) {
          await this.redirectToAfterSignUp({ replace: true });
        } else {
          await this.redirectToAfterSignIn({ replace: true });
        }
      }
      return Result.ok(void 0);
    } else {
      return Result.error(result.error);
    }
  }
  /**
   * Initiates a CLI authentication process that allows a command line application
   * to get a refresh token for a user's account.
   *
   * This process works as follows:
   * 1. The CLI app calls this method, which initiates the auth process with the server
   * 2. The server returns a polling code and a login code
   * 3. The CLI app opens a browser window to the appUrl with the login code as a parameter
   * 4. The user logs in through the browser and confirms the authorization
   * 5. The CLI app polls for the refresh token using the polling code
   *
   * @param options Options for the CLI login
   * @param options.appUrl The URL of the app that will handle the CLI auth confirmation
   * @param options.expiresInMillis Optional duration in milliseconds before the auth attempt expires (default: 2 hours)
   * @param options.maxAttempts Optional maximum number of polling attempts (default: Infinity)
   * @param options.waitTimeMillis Optional time to wait between polling attempts (default: 2 seconds)
   * @param options.promptLink Optional function to call with the login URL to prompt the user to open the browser
   * @returns Result containing either the refresh token or an error
   */
  async promptCliLogin(options) {
    const response = await this._interface.sendClientRequest(
      "/auth/cli",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          expires_in_millis: options.expiresInMillis
        })
      },
      null
    );
    if (!response.ok) {
      return Result.error(new KnownErrors.CliAuthError(`Failed to initiate CLI auth: ${response.status} ${await response.text()}`));
    }
    const initResult = await response.json();
    const pollingCode = initResult.polling_code;
    const loginCode = initResult.login_code;
    const url = `${options.appUrl}/handler/cli-auth-confirm?login_code=${encodeURIComponent(loginCode)}`;
    if (options.promptLink) {
      options.promptLink(url);
    } else {
      console.log(`Please visit the following URL to authenticate:
${url}`);
    }
    let attempts = 0;
    while (attempts < (options.maxAttempts ?? Infinity)) {
      attempts++;
      const pollResponse = await this._interface.sendClientRequest("/auth/cli/poll", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          polling_code: pollingCode
        })
      }, null);
      if (!pollResponse.ok) {
        return Result.error(new KnownErrors.CliAuthError(`Failed to initiate CLI auth: ${pollResponse.status} ${await pollResponse.text()}`));
      }
      const pollResult = await pollResponse.json();
      if (pollResponse.status === 201 && pollResult.status === "success") {
        return Result.ok(pollResult.refresh_token);
      }
      if (pollResult.status === "waiting") {
        await wait(options.waitTimeMillis ?? 2e3);
        continue;
      }
      if (pollResult.status === "expired") {
        return Result.error(new KnownErrors.CliAuthExpiredError("CLI authentication request expired. Please try again."));
      }
      if (pollResult.status === "used") {
        return Result.error(new KnownErrors.CliAuthUsedError("This authentication token has already been used."));
      }
      return Result.error(new KnownErrors.CliAuthError(`Unexpected status from CLI auth polling: ${pollResult.status}`));
    }
    return Result.error(new KnownErrors.CliAuthError("Timed out waiting for CLI authentication."));
  }
  /*
   * Completes the MFA sign-in process by verifying the provided OTP code
   * @param totp The TOTP (Time-based One-Time Password) provided by the user
   * @param code The Attempt code provided by the user
   * @param options Additional options for the sign-in process
   * @returns A Result indicating success or failure
   */
  async signInWithMfa(totp, code, options) {
    this._ensurePersistentTokenStore();
    const session = await this._getSession();
    let result;
    try {
      result = await this._catchMfaRequiredError(async () => {
        return await this._interface.signInWithMfa(totp, code, session);
      });
    } catch (e) {
      if (e instanceof KnownErrors.InvalidTotpCode) {
        return Result.error(e);
      }
      throw e;
    }
    if (result.status === "ok") {
      await this._signInToAccountWithTokens(result.data);
      if (!options?.noRedirect) {
        if (result.data.newUser) {
          await this.redirectToAfterSignUp({ replace: true });
        } else {
          await this.redirectToAfterSignIn({ replace: true });
        }
      }
      return Result.ok(void 0);
    }
    return Result.error(result.error);
  }
  async signInWithPasskey() {
    this._ensurePersistentTokenStore();
    const session = await this._getSession();
    let result;
    try {
      result = await this._catchMfaRequiredError(async () => {
        const initiationResult = await this._interface.initiatePasskeyAuthentication({}, session);
        if (initiationResult.status !== "ok") {
          return Result.error(new KnownErrors.PasskeyAuthenticationFailed("Failed to get initiation options for passkey authentication"));
        }
        const { options_json, code } = initiationResult.data;
        if (options_json.rpId !== "THIS_VALUE_WILL_BE_REPLACED.example.com") {
          throw new StackAssertionError(`Expected returned RP ID from server to equal sentinel, but found ${options_json.rpId}`);
        }
        options_json.rpId = window.location.hostname;
        const authentication_response = await startAuthentication({ optionsJSON: options_json });
        return await this._interface.signInWithPasskey({ authentication_response, code }, session);
      });
    } catch (error) {
      if (error instanceof WebAuthnError) {
        return Result.error(new KnownErrors.PasskeyWebAuthnError(error.message, error.name));
      } else {
        return Result.error(new KnownErrors.PasskeyAuthenticationFailed("Failed to sign in with passkey"));
      }
    }
    if (result.status === "ok") {
      await this._signInToAccountWithTokens(result.data);
      await this.redirectToAfterSignIn({ replace: true });
      return Result.ok(void 0);
    } else {
      return Result.error(result.error);
    }
  }
  async callOAuthCallback() {
    if (typeof window === "undefined") {
      throw new Error("callOAuthCallback can currently only be called in a browser environment");
    }
    this._ensurePersistentTokenStore();
    let result;
    try {
      result = await this._catchMfaRequiredError(async () => {
        return await callOAuthCallback(this._interface, this.urls.oauthCallback);
      });
    } catch (e) {
      if (KnownErrors.InvalidTotpCode.isInstance(e)) {
        alert("Invalid TOTP code. Please try signing in again.");
        return false;
      } else {
        throw e;
      }
    }
    if (result.status === "ok" && result.data) {
      await this._signInToAccountWithTokens(result.data);
      if ("afterCallbackRedirectUrl" in result.data && result.data.afterCallbackRedirectUrl) {
        await this._redirectTo({ url: result.data.afterCallbackRedirectUrl, replace: true });
        return true;
      } else if (result.data.newUser) {
        await this.redirectToAfterSignUp({ replace: true });
        return true;
      } else {
        await this.redirectToAfterSignIn({ replace: true });
        return true;
      }
    }
    return false;
  }
  async _signOut(session, options) {
    await storeLock.withWriteLock(async () => {
      await this._interface.signOut(session);
      if (options?.redirectUrl) {
        await this._redirectTo({ url: options.redirectUrl, replace: true });
      } else {
        await this.redirectToAfterSignOut();
      }
    });
  }
  async signOut(options) {
    const user = await this.getUser({ tokenStore: options?.tokenStore ?? void 0 });
    if (user) {
      await user.signOut({ redirectUrl: options?.redirectUrl });
    }
  }
  async getAccessToken(options) {
    const user = await this.getUser({ tokenStore: options?.tokenStore ?? void 0 });
    if (user) {
      return await user.getAccessToken();
    }
    return null;
  }
  useAccessToken(options) {
    const user = this.useUser({ tokenStore: options?.tokenStore ?? void 0 });
    if (user) {
      return user.useAccessToken();
    }
    return null;
  }
  async getRefreshToken(options) {
    const user = await this.getUser({ tokenStore: options?.tokenStore ?? void 0 });
    if (user) {
      return await user.getRefreshToken();
    }
    return null;
  }
  useRefreshToken(options) {
    const user = this.useUser({ tokenStore: options?.tokenStore ?? void 0 });
    if (user) {
      return user.useRefreshToken();
    }
    return null;
  }
  async getAuthHeaders(options) {
    return {
      "x-stack-auth": JSON.stringify(await this.getAuthJson(options))
    };
  }
  useAuthHeaders(options) {
    return {
      "x-stack-auth": JSON.stringify(this.useAuthJson(options))
    };
  }
  async getAuthJson(options) {
    const user = await this.getUser({ tokenStore: options?.tokenStore ?? void 0 });
    if (user) {
      return await user.getAuthJson();
    }
    return { accessToken: null, refreshToken: null };
  }
  useAuthJson(options) {
    const user = this.useUser({ tokenStore: options?.tokenStore ?? void 0 });
    if (user) {
      return user.useAuthJson();
    }
    return { accessToken: null, refreshToken: null };
  }
  async getProject() {
    const crud = Result.orThrow(await this._currentProjectCache.getOrWait([], "write-only"));
    return this._clientProjectFromCrud(crud);
  }
  useProject() {
    const crud = useAsyncCache(this._currentProjectCache, [], "clientApp.useProject()");
    return useMemo(() => this._clientProjectFromCrud(crud), [crud]);
  }
  async _listOwnedProjects(session) {
    this._ensureInternalProject();
    const crud = Result.orThrow(await this._ownedProjectsCache.getOrWait([session], "write-only"));
    return crud.map((j) => this._getOwnedAdminApp(j.id, session)._adminOwnedProjectFromCrud(
      j,
      () => this._refreshOwnedProjects(session)
    ));
  }
  _useOwnedProjects(session) {
    this._ensureInternalProject();
    const projects = useAsyncCache(this._ownedProjectsCache, [session], "clientApp.useOwnedProjects()");
    return useMemo(() => projects.map((j) => this._getOwnedAdminApp(j.id, session)._adminOwnedProjectFromCrud(
      j,
      () => this._refreshOwnedProjects(session)
    )), [projects]);
  }
  async _createProject(session, newProject) {
    this._ensureInternalProject();
    const crud = await this._interface.createProject(adminProjectCreateOptionsToCrud(newProject), session);
    const res = this._getOwnedAdminApp(crud.id, session)._adminOwnedProjectFromCrud(
      crud,
      () => this._refreshOwnedProjects(session)
    );
    await this._refreshOwnedProjects(session);
    return res;
  }
  async _refreshUser(session) {
    await this._refreshSession(session);
  }
  async _refreshSession(session) {
    await this._currentUserCache.refresh([session]);
    session.suggestAccessTokenExpired();
  }
  async _refreshUsers() {
  }
  async _refreshProject() {
    await this._currentProjectCache.refresh([]);
  }
  async _refreshOwnedProjects(session) {
    await this._ownedProjectsCache.refresh([session]);
  }
  static get [stackAppInternalsSymbol]() {
    return {
      fromClientJson: (json) => {
        const providedCheckString = JSON.stringify(omit(json, [
          /* none currently */
        ]));
        const existing = allClientApps.get(json.uniqueIdentifier);
        if (existing) {
          const [existingCheckString, clientApp] = existing;
          if (existingCheckString !== void 0 && existingCheckString !== providedCheckString) {
            throw new StackAssertionError("The provided app JSON does not match the configuration of the existing client app with the same unique identifier", { providedObj: json, existingString: existingCheckString });
          }
          return clientApp;
        }
        return new __StackClientAppImplIncomplete({
          ...omit(json, ["uniqueIdentifier"])
        }, {
          uniqueIdentifier: json.uniqueIdentifier,
          checkString: providedCheckString
        });
      }
    };
  }
  get [stackAppInternalsSymbol]() {
    return {
      toClientJson: () => {
        if (!("publishableClientKey" in this._interface.options)) {
          throw new StackAssertionError("Cannot serialize to JSON from an application without a publishable client key");
        }
        if (typeof this._redirectMethod !== "string") {
          throw new StackAssertionError("Cannot serialize to JSON from an application with a non-string redirect method");
        }
        return {
          baseUrl: this._options.baseUrl,
          projectId: this.projectId,
          publishableClientKey: this._interface.options.publishableClientKey,
          tokenStore: this._tokenStoreInit,
          urls: this._urlOptions,
          oauthScopesOnSignIn: this._oauthScopesOnSignIn,
          uniqueIdentifier: this._getUniqueIdentifier(),
          redirectMethod: this._redirectMethod,
          extraRequestHeaders: this._options.extraRequestHeaders
        };
      },
      setCurrentUser: (userJsonPromise) => {
        runAsynchronously(async () => {
          await this._currentUserCache.forceSetCachedValueAsync([await this._getSession()], Result.fromPromise(userJsonPromise));
        });
      },
      getConstructorOptions: () => this._options,
      sendRequest: async (path, requestOptions, requestType = "client") => {
        return await this._interface.sendClientRequest(path, requestOptions, await this._getSession(), requestType);
      }
    };
  }
};
/**
 * There is a circular dependency between the admin app and the client app, as the former inherits from the latter and
 * the latter needs to use the former when creating a new instance of an internal project.
 *
 * To break it, we set the admin app here lazily instead of importing it directly. This variable is set by ./index.ts,
 * which imports both this file and ./admin-app-impl.ts.
 */
__StackClientAppImplIncomplete.LazyStackAdminAppImpl = { value: void 0 };
var _StackClientAppImplIncomplete = __StackClientAppImplIncomplete;
export {
  _StackClientAppImplIncomplete
};
//# sourceMappingURL=client-app-impl.js.map